// src/pages/EditProperty.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { suggestReferenceNumbers } from '../shared/utils/reference-number-helper';
import {GET_PROPERTY, UPDATE_PROPERTY} from "../graphql/properties";
import {GET_LANDLORD, GET_ALL_LANDLORDS} from "../graphql/landlords";
import {GET_ALL_AGENTS} from "../graphql/agents";
import {GET_TENANT, GET_ALL_TENANTS} from "../graphql/tenants";

const EditProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    street: '',
    suburb: '',
    town: '',
    province: '',
    postalCode: '',
    status: 'Vacant Rental',
    description: '',
    referenceNumber: '',
    electricityMeterNumber: '',
    bedrooms: '',
    bathrooms: '',
    garages: '',
    landlordId: '',
    agentId: '',
    tenantId: ''
  });
  
  const [errors, setErrors] = useState({});
  const [suggestedReferences, setSuggestedReferences] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Fetch property data
  const { loading: loadingProperty, error: errorProperty } = useQuery(GET_PROPERTY, {
    variables: { id },
    onCompleted: (data) => {
      const property = data.property;
      setFormData({
        street: property.address.street || '',
        suburb: property.address.suburb || '',
        town: property.address.city || '',
        province: property.address.province || '',
        postalCode: property.address.postalCode || '',
        status: property.status || 'vacant',
        description: property.description || '',
        referenceNumber: property.referenceNumber || '',
        electricityMeterNumber: property.electricityMeterNumber || '',
        bedrooms: property.bedrooms !== null ? property.bedrooms.toString() : '',
        bathrooms: property.bathrooms !== null ? property.bathrooms.toString() : '',
        garages: property.garages !== null ? property.garages.toString() : '',
        landlordId: property.landlord ? property.landlord.id : '',
        tenantId: property.tenant ? property.tenant.id : '',
        agentId: property.agent ? property.agent.id : ''
      });
    }
  });
  
  // Fetch selection data
  const { loading: loadingLandlords, data: landlordsData } = useQuery(GET_ALL_LANDLORDS);
  const { loading: loadingAgents, data: agentsData } = useQuery(GET_ALL_AGENTS);
  const { loading: loadingTenants, data: tenantsData } = useQuery(GET_ALL_TENANTS);

  // Additional queries for reference number generation
  const { data: landlordData } = useQuery(GET_LANDLORD, {
    variables: { id: formData.landlordId },
    skip: !formData.landlordId
  });

  const { data: tenantData } = useQuery(GET_TENANT, {
    variables: { id: formData.tenantId },
    skip: !formData.tenantId
  });
  
  // Update suggestions whenever landlord, tenant or street changes
  useEffect(() => {
    if (landlordData?.landlord) {
      const landlord = landlordData.landlord;
      const tenant = tenantData?.tenant || null;
      const street = formData.street || '';
      
      const suggestions = suggestReferenceNumbers(landlord, tenant, street);
      setSuggestedReferences(suggestions);
    } else {
      setSuggestedReferences([]);
    }
  }, [landlordData, tenantData, formData.street]);
  
  // Update mutation
  const [updateProperty, { loading: loadingUpdate }] = useMutation(UPDATE_PROPERTY, {
    onCompleted: (data) => {
      navigate(`/properties/${data.updateProperty.id}`);
    },
    onError: (error) => {
      console.error('Error updating property:', error);
      // Display error message in the UI
      setErrors({
        ...errors,
        submit: error.message
      });
    }
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleReferenceSelect = (reference) => {
    setFormData({
      ...formData,
      referenceNumber: reference
    });
    setShowSuggestions(false);
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.street.trim()) {
      newErrors.street = 'Street address is required';
    }
    
    if (!formData.suburb.trim()) {
      newErrors.suburb = 'Suburb is required';
    }
    
    if (!formData.town.trim()) {
      newErrors.town = 'Town is required';
    }
    
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required';
    }
    
    if (!formData.landlordId) {
      newErrors.landlordId = 'Landlord selection is required';
    }
    
    // Validate numeric fields
    if (formData.bedrooms && !/^\d+$/.test(formData.bedrooms)) {
      newErrors.bedrooms = 'Bedrooms must be a number';
    }
    
    if (formData.bathrooms && !/^\d+$/.test(formData.bathrooms)) {
      newErrors.bathrooms = 'Bathrooms must be a number';
    }
    
    if (formData.garages && !/^\d+$/.test(formData.garages)) {
      newErrors.garages = 'Garages must be a number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const mutationInput = {
        address: {
          street: formData.street,
          suburb: formData.suburb,
          city: formData.town,
          province: formData.province || 'Not specified',
          postalCode: formData.postalCode
        },
        status: formData.status,
        description: formData.description || '',
        referenceNumber: formData.referenceNumber || null,
        electricityMeterNumber: formData.electricityMeterNumber || null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms, 10) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms, 10) : null,
        garages: formData.garages ? parseInt(formData.garages, 10) : null,
        landlord: formData.landlordId,
        agent: formData.agentId || null,
        tenant: formData.tenantId || null
      };
      
      updateProperty({
        variables: {
          id,
          input: mutationInput
        }
      });
    }
  };
  
  if (loadingProperty) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (errorProperty) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {errorProperty.message}</span>
      </div>
    );
  }
  
  // Prepare selection options
  const landlordOptions = landlordsData?.landlords?.items || [];
  const agentOptions = agentsData?.agents?.items || [];
  const tenantOptions = tenantsData?.tenants?.items || [];
  
  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit Property
        </h1>
        <Link to={`/properties/${id}`} className="flex items-center text-blue-600 hover:text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Property
        </Link>
      </div>
      
      {errors.submit && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {errors.submit}</span>
        </div>
      )}
      
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Property Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reference Number */}
              <div className="relative">
                <label htmlFor="referenceNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="referenceNumber"
                    name="referenceNumber"
                    value={formData.referenceNumber}
                    onChange={handleChange}
                    className={`block w-full px-3 py-2 border ${errors.referenceNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    placeholder="e.g. TEN001-LAND002"
                    onFocus={() => formData.landlordId && setShowSuggestions(true)}
                  />
                  {formData.landlordId && (
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(!showSuggestions)}
                      className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                {errors.referenceNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.referenceNumber}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Optional. Create a unique reference for this property.
                </p>
                
                {/* Reference suggestions dropdown */}
                {showSuggestions && suggestedReferences.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {suggestedReferences.map((reference, index) => (
                      <div
                        key={index}
                        className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                        onClick={() => handleReferenceSelect(reference)}
                      >
                        {reference}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Electricity Meter Number */}
              <div>
                <label htmlFor="electricityMeterNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Electricity Meter Number
                </label>
                <input
                  type="text"
                  id="electricityMeterNumber"
                  name="electricityMeterNumber"
                  value={formData.electricityMeterNumber}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.electricityMeterNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="e.g. 12345678"
                />
                {errors.electricityMeterNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.electricityMeterNumber}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Unique municipal electricity meter number for this property.
                </p>
              </div>
              
              {/* Street */}
              <div className="md:col-span-2">
                <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address*
                </label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.street ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.street && (
                  <p className="mt-1 text-sm text-red-600">{errors.street}</p>
                )}
              </div>
              
              {/* Suburb */}
              <div>
                <label htmlFor="suburb" className="block text-sm font-medium text-gray-700 mb-1">
                  Suburb*
                </label>
                <input
                  type="text"
                  id="suburb"
                  name="suburb"
                  value={formData.suburb}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.suburb ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.suburb && (
                  <p className="mt-1 text-sm text-red-600">{errors.suburb}</p>
                )}
              </div>
              
              {/* Town */}
              <div>
                <label htmlFor="town" className="block text-sm font-medium text-gray-700 mb-1">
                  Town*
                </label>
                <input
                  type="text"
                  id="town"
                  name="town"
                  value={formData.town}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.town ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.town && (
                  <p className="mt-1 text-sm text-red-600">{errors.town}</p>
                )}
              </div>
              
              {/* Postal Code */}
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code*
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.postalCode ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.postalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Property Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="Vacant Rental">Vacant</option>
                  <option value="Rented By Us">Occupied</option>
                  <option value="Rental Application In Progress">In Progress</option>
                </select>
              </div>
              
              {/* Bedrooms */}
              <div>
                <label htmlFor="bedrooms" className="block text-sm font-medium text-gray-700 mb-1">
                  Bedrooms
                </label>
                <input
                  type="text"
                  id="bedrooms"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.bedrooms ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.bedrooms && (
                  <p className="mt-1 text-sm text-red-600">{errors.bedrooms}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="bathrooms" className="block text-sm font-medium text-gray-700 mb-1">
                  Bathrooms
                </label>
                <input
                  type="text"
                  id="bathrooms"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.bathrooms ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.bathrooms && (
                  <p className="mt-1 text-sm text-red-600">{errors.bathrooms}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="garages" className="block text-sm font-medium text-gray-700 mb-1">
                  Garages
                </label>
                <input
                  type="text"
                  id="garages"
                  name="garages"
                  value={formData.garages}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.garages ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.garages && (
                  <p className="mt-1 text-sm text-red-600">{errors.garages}</p>
                )}
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Associations</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Landlord */}
              <div>
                <label htmlFor="landlordId" className="block text-sm font-medium text-gray-700 mb-1">
                  Landlord*
                </label>
                <select
                  id="landlordId"
                  name="landlordId"
                  value={formData.landlordId}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.landlordId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  disabled={loadingLandlords}
                >
                  <option value="">Select Landlord</option>
                  {landlordOptions.map(landlord => (
                    <option key={landlord.id} value={landlord.id}>
                      {landlord.firstName} {landlord.lastName}
                    </option>
                  ))}
                </select>
                {errors.landlordId && (
                  <p className="mt-1 text-sm text-red-600">{errors.landlordId}</p>
                )}
                {loadingLandlords && (
                  <p className="mt-1 text-sm text-gray-500">Loading landlords...</p>
                )}
              </div>

              {/* Tenant */}
              <div>
                <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant (Optional)
                </label>
                <select
                  id="tenantId"
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={loadingTenants}
                >
                  <option value="">Select Tenant</option>
                  {tenantOptions.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.firstName} {tenant.lastName}
                    </option>
                  ))}
                </select>
                {loadingTenants && (
                  <p className="mt-1 text-sm text-gray-500">Loading tenants...</p>
                )}
              </div>
              
              {/* Agent */}
              <div>
                <label htmlFor="agentId" className="block text-sm font-medium text-gray-700 mb-1">
                  Agent (Optional)
                </label>
                <select
                  id="agentId"
                  name="agentId"
                  value={formData.agentId}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={loadingAgents}
                >
                  <option value="">Select Agent</option>
                  {agentOptions.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.firstName} {agent.lastName}
                    </option>
                  ))}
                </select>
                {loadingAgents && (
                  <p className="mt-1 text-sm text-gray-500">Loading agents...</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/properties/${id}`)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            <button
              type="submit"
              disabled={loadingUpdate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loadingUpdate ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditProperty;